/**
 * Account Flags Hook
 * Fetches and stores the current account's feature flags globally.
 * This is the first API call after login — fetched in ProtectedRoute.
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { accountService } from '@/services';
import { isAuthenticatedAtom } from '@/hooks/use-auth';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';

export interface AccountFlags {
  shadow_mode_enabled?: boolean;
  social_agent_enabled?: boolean;
  [key: string]: boolean | string | number | undefined;
}

// Query atom — runs once authenticated
export const accountFlagsQueryAtom = atomWithQuery((get) => {
  const isAuthenticated = get(isAuthenticatedAtom);
  return {
    // Brand-keyed because one flag — the intent-filtering master switch — is per brand.
    // The backend resolves each key against the brand, and only keys a brand has actually
    // saved differ, so every other flag reads exactly as it did before.
    queryKey: ['account', 'flags', buScopeKey(get)],
    queryFn: async () => {
      const data = await accountService.getAccountFlags(buScopeParams(get));
      return data.flags as AccountFlags;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  };
});

export function useAccountFlags() {
  const [query] = useAtom(accountFlagsQueryAtom);
  return {
    flags: (query.data ?? {}) as AccountFlags,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch
  };
}

const updateKillSwitchMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account', 'kill-switch'],
  // The brand goes with the WRITE, not just the read. The query atom above has been
  // brand-keyed since intent filtering landed; this mutation was not, so on a brand that owns
  // its own value the toggle wrote the account and the page re-read the brand — unchanged.
  mutationFn: (enabled: boolean) => accountService.updateKillSwitch(enabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account', 'flags']);
  }
}));

export function useUpdateKillSwitch() {
  const [mutation] = useAtom(updateKillSwitchMutationAtom);
  return mutation;
}

const brandFlagMutationAtom = (endpoint: string) =>
  atomWithMutation((get) => ({
    mutationKey: ['account', endpoint],
    mutationFn: (enabled: boolean) => accountService.updateBrandFlag(endpoint, enabled, buScopeParams(get)),
    onSuccess: () => {
      invalidateQueries(['account', 'flags']);
    }
  }));

const updateShadowModeMutationAtom = brandFlagMutationAtom(API_ENDPOINTS.ACCOUNTS.SHADOW_MODE);
const updateAiEnabledMutationAtom = brandFlagMutationAtom(API_ENDPOINTS.ACCOUNTS.AI_ENABLED);

export function useUpdateShadowMode() {
  const [mutation] = useAtom(updateShadowModeMutationAtom);
  return mutation;
}

export function useUpdateAiEnabled() {
  const [mutation] = useAtom(updateAiEnabledMutationAtom);
  return mutation;
}
